/**
 * ET - Cart Drawer
 * Extends the theme CartDrawer (assets/theme.js) so every theme cart contract keeps working
 * (cart:change, cart:refresh, variant:add, line-item, line-item-quantity, product-form...).
 * Cart changes patch regions in place so the free shipping bar animates and the USP rotator keeps running.
 */
(() => {
  const MOBILE_QUERY = 'screen and (max-width: 749px)';
  const LINE_ITEM_CHANGE_DELAY = 1250;
  // A re-keyed line has no removal animation to wait for beyond the theme's 200ms collapse.
  const REKEY_RENDER_DELAY = 250;
  const FOCUSABLE_SELECTOR = 'a[href], button, input, textarea, select, summary, [tabindex]';
  // Room kept around a revealed control so its focus ring (2px outline + 2px offset) is not clipped by the scroll part.
  const FOCUS_REVEAL_MARGIN = 4;

  // Background images: their layer custom properties live behind this class, so nothing is requested while the
  // drawer is closed. The first sign the customer is heading for the cart adds it.
  const BG_READY_CLASS = 'et-cart-drawer--bg-ready';
  const BG_WARM_EVENTS = ['pointerover', 'focusin', 'touchstart'];
  // The idle warm is armed by the FIRST sign of engagement, never on page load: a visitor who bounces must not pay
  // for an image they never see, while anyone who actually interacts has the layers decoded before the drawer can be
  // opened from a product form (cart:add does not pass through a cart trigger).
  const BG_ENGAGE_EVENTS = ['pointerdown', 'keydown', 'scroll'];
  const BG_IDLE_TIMEOUT = 4000;
  const BG_IDLE_FALLBACK_DELAY = 2000;
  const CART_TRIGGER_SELECTOR =
    '[data-et-cart-trigger], [aria-controls="cart-drawer"], .header__icon--cart, a[href$="/cart"], a[href*="/cart?"]';
  // Fallback when no data-et-usp-delay is present (a footer rendered before this script was updated).
  const USP_AUTOPLAY_DELAY = 4000;

  // Controls that keep their identity across re-renders, matched inside their line item, upsell card or footer part.
  const FOCUS_CONTROLS = [
    '[data-et-stepper="minus"]',
    '[data-et-stepper="plus"]',
    'input[data-line-key]',
    '.et-cart-drawer__remove',
    '.et-cart-drawer__line-item-title',
    '.et-cart-drawer__chip-link',
    '.et-cart-drawer__upsell-title',
    '.et-cart-drawer__upsell-button',
    '.et-cart-drawer__note-toggle',
    '.et-cart-drawer__note-input',
    '.et-cart-drawer__checkout',
    '.et-cart-drawer__trustpilot',
    '.et-cart-drawer__empty-button',
    '.et-cart-drawer__close'
  ];

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getSectionId = (element) => {
    const section = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  const toFragment = (html) => document.createRange().createContextualFragment(html);

  const parseDrawer = (html) => new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer');

  const isFocusable = (element) =>
    Boolean(element) && element.isConnected && !element.disabled && element.getClientRects().length > 0;

  // Keys of the following siblings (in order), then of the preceding ones (nearest first).
  const neighbourKeys = (element, attribute) => {
    const keys = [];
    const selector = `[${attribute}]`;
    const read = (node) => node.getAttribute(attribute);

    for (let node = element.nextElementSibling; node; node = node.nextElementSibling) {
      if (node.matches(selector)) keys.push(read(node));
    }

    for (let node = element.previousElementSibling; node; node = node.previousElementSibling) {
      if (node.matches(selector)) keys.push(read(node));
    }

    return keys;
  };

  const whenSwiperReady = () =>
    new Promise((resolve) => {
      if (typeof window.Swiper === 'function') {
        resolve(window.Swiper);
        return;
      }

      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;

        if (typeof window.Swiper === 'function') {
          clearInterval(timer);
          resolve(window.Swiper);
        } else if (attempts > 200) {
          clearInterval(timer);
          resolve(null);
        }
      }, 50);
    });

  const defineEtCartDrawer = () => {
    if (window.customElements.get('et-cart-drawer')) {
      return;
    }

    const CartDrawer = window.customElements.get('cart-drawer');

    class EtCartDrawer extends CartDrawer {
      constructor() {
        super();
        this._etSwipers = new Map();
        this._etRenderGeneration = 0;
        this._etMediaQuery = window.matchMedia(MOBILE_QUERY);
        this._etOnBreakpointChange = this._etInitSwipers.bind(this);
        this._etOnToggle = this._etOnToggle.bind(this);
        this._etOnStepperClick = this._etOnStepperClick.bind(this);
        this._etOnLineItemError = this._etOnLineItemError.bind(this);
        this._etOnLineItemWillChange = this._etOnLineItemWillChange.bind(this);
        this._etOnLineItemChange = this._etOnLineItemChange.bind(this);
        this._etOnUspInteraction = this._etOnUspInteraction.bind(this);
        this._etOnFocusIn = this._etOnFocusIn.bind(this);
        this._etOnFocusOut = this._etOnFocusOut.bind(this);
        this._etOnPointerDown = this._etOnPointerDown.bind(this);
        this._etOnVariantAdd = this._etOnVariantAdd.bind(this);
        this._etOnBlockSelect = this._etOnBlockSelect.bind(this);
        this._etOnBlockDeselect = this._etOnBlockDeselect.bind(this);
        this._etWarmBackgrounds = this._etWarmBackgrounds.bind(this);
        this._etOnWarmTrigger = this._etOnWarmTrigger.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        this._etAbortController = new AbortController();
        const { signal } = this._etAbortController;

        this._etMediaQuery.addEventListener('change', this._etOnBreakpointChange, { signal });
        this.addEventListener('toggle', this._etOnToggle, { signal });
        this.addEventListener('click', this._etOnStepperClick, { capture: true, signal });
        this.addEventListener('line-item:error', this._etOnLineItemError, { signal });
        this.addEventListener('line-item:will-change', this._etOnLineItemWillChange, { signal });
        this.addEventListener('line-item:change', this._etOnLineItemChange, { signal });
        this.addEventListener('focusin', this._etOnUspInteraction, { signal });
        this.addEventListener('pointerdown', this._etOnUspInteraction, { signal });
        this.addEventListener('focusin', this._etOnFocusIn, { signal });
        this.addEventListener('focusout', this._etOnFocusOut, { signal });
        this.addEventListener('pointerdown', this._etOnPointerDown, { capture: true, signal });
        this.addEventListener('variant:add', this._etOnVariantAdd, { capture: true, signal });

        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', this._etOnBlockSelect, { signal });
          this.addEventListener('shopify:block:deselect', this._etOnBlockDeselect, { signal });
        }

        this._etSetupBackgroundWarmUp(signal);
        this._etAfterRender();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this._etAbortController?.abort();
        this._etBgAbortController?.abort();
        clearTimeout(this._etRenderTimer);
        clearTimeout(this._etBgIdleTimer);
        this._etDestroySwipers();
      }

      /* Background images: lazy by default ------------------------------- */

      /* The section only emits --et-*-bg-layers-* inside .et-cart-drawer--bg-ready, so until this runs every
         surface computes background-image: none and no request is made for a closed drawer. */
      _etWarmBackgrounds() {
        if (this._etBgReady) {
          return;
        }

        this._etBgReady = true;
        this.classList.add(BG_READY_CLASS);
        this._etBgAbortController?.abort();
        this._etBgAbortController = null;
        clearTimeout(this._etBgIdleTimer);
      }

      _etOnWarmTrigger(event) {
        const target = event.target;

        if (target && typeof target.closest === 'function' && target.closest(CART_TRIGGER_SELECTOR)) {
          this._etWarmBackgrounds();
        }
      }

      _etSetupBackgroundWarmUp(signal) {
        // data-et-bg-images is only present when at least one background image setting is filled in.
        if (!this.hasAttribute('data-et-bg-images') || this._etBgReady) {
          return;
        }

        // The theme editor must show the image while it is being chosen.
        if (window.Shopify && window.Shopify.designMode) {
          this._etWarmBackgrounds();
          return;
        }

        this._etBgAbortController = new AbortController();
        const bgSignal = this._etBgAbortController.signal;

        signal.addEventListener('abort', () => this._etBgAbortController?.abort(), { once: true });

        BG_WARM_EVENTS.forEach((type) => {
          document.addEventListener(type, this._etOnWarmTrigger, { capture: true, passive: true, signal: bgSignal });
        });

        // Backstop: a drawer that opens by any other route (cart:add, a deep link) still warms.
        this.addEventListener('toggle', this._etWarmBackgrounds, { signal: bgSignal });

        let bgScheduled = false;
        const schedule = () => {
          if (bgScheduled || this._etBgReady) {
            return;
          }

          bgScheduled = true;

          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(this._etWarmBackgrounds, { timeout: BG_IDLE_TIMEOUT });
          } else {
            this._etBgIdleTimer = setTimeout(this._etWarmBackgrounds, BG_IDLE_FALLBACK_DELAY);
          }
        };

        BG_ENGAGE_EVENTS.forEach((type) => {
          document.addEventListener(type, schedule, { capture: true, passive: true, once: true, signal: bgSignal });
        });
      }

      /* Open without focusing a quantity input (the theme focuses the first input, which selects its value and paints
         a focus ring). The close button only shows a ring when the drawer was opened from the keyboard. Returned as a
         function so focus-trap resolves the current button, not one replaced by a re-render. */
      get initialFocus() {
        return () => this.querySelector('.et-cart-drawer__close') || false;
      }

      /* Hide the aside column when its slot is empty (the theme only handles header/body/footer). */
      _updateSlotVisibility(slot) {
        if (slot.name === 'aside') {
          slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
          return;
        }

        super._updateSlotVisibility(slot);
      }

      async _onCartChanged(event) {
        // Every cart change or refresh invalidates renders that are still waiting (empty fade, section fetch).
        this._etRenderGeneration += 1;
        this._etCancelFade();

        const rekeyed = this._etRekeyPending === true;
        this._etRekeyPending = false;

        const cart = event.detail?.cart;
        const sectionId = getSectionId(this);
        const html = cart?.sections?.[sectionId];
        const pendingNote = this._etReconcileNote(cart);

        if (!cart || !html) {
          this._onCartRefresh();
          return;
        }

        const nextDrawer = parseDrawer(html);

        if (!nextDrawer) {
          return;
        }

        if (cart.item_count === 0) {
          this._etRenderEmpty(nextDrawer);
          return;
        }

        if (!this.querySelector('.et-cart-drawer__line-items')) {
          clearTimeout(this._etRenderTimer);
          this._etReplaceAll(nextDrawer);
          this._etApplyNote(pendingNote);
          return;
        }

        this._etPatchShipping(nextDrawer);
        this._etPatchFooter(nextDrawer);
        this._etApplyNote(pendingNote);

        clearTimeout(this._etRenderTimer);
        this._etRenderTimer = setTimeout(
          () => this._etPatchContent(nextDrawer),
          event.detail.baseEvent === 'variant:add' ? 0 : rekeyed ? REKEY_RENDER_DELAY : LINE_ITEM_CHANGE_DELAY
        );
      }

      async _onCartRefresh() {
        const sectionId = getSectionId(this);

        if (!sectionId) {
          return;
        }

        // Only a successful, still-current response may cancel pending renders; a failed request keeps them.
        const token = (this._etRefreshToken || 0) + 1;
        const generation = this._etRenderGeneration;
        this._etRefreshToken = token;

        let html;

        try {
          // A note save started by the drawer must land before the section renders cart.note.
          await this._etNoteQueue;

          const response = await fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`);

          if (!response.ok) {
            return;
          }

          html = await response.text();
        } catch (error) {
          return;
        }

        // A newer cart change or refresh started while this request was in flight: its content wins.
        if (token !== this._etRefreshToken || generation !== this._etRenderGeneration) {
          return;
        }

        const nextDrawer = parseDrawer(html);

        if (!nextDrawer) {
          return;
        }

        this._etRenderGeneration += 1;
        clearTimeout(this._etRenderTimer);
        this._etCancelFade();
        this._etReplaceAll(nextDrawer);
      }

      /* Rendering --------------------------------------------------------- */

      _etAfterRender() {
        this._etApplyShippingState(this.querySelector('[data-et-shipping]'));
        this._etInitSwipers();
      }

      _etSyncHostState(nextDrawer) {
        ['et-cart-drawer--has-aside', 'et-cart-drawer--empty'].forEach((className) => {
          this.classList.toggle(className, nextDrawer.classList.contains(className));
        });
      }

      _etReplaceAll(nextDrawer) {
        const focus = this._etCaptureFocus([this]);

        this._etDestroySwipers();
        this._etSyncHostState(nextDrawer);
        this.replaceChildren(toFragment(nextDrawer.innerHTML));
        this._etAfterRender();
        this._etRestoreFocus(focus);
      }

      _etCancelFade() {
        if (!this._etFadeAnimations) {
          return;
        }

        this._etFadeAnimations.forEach((animation) => animation.cancel());
        this._etFadeAnimations = null;
      }

      async _etRenderEmpty(nextDrawer) {
        clearTimeout(this._etRenderTimer);

        const generation = this._etRenderGeneration;
        const reduceMotion = prefersReducedMotion();
        const children = Array.from(this.children);

        if (!reduceMotion && children.length > 0) {
          const animations = children.map((child) => child.animate({ opacity: [1, 0] }, { duration: 150, fill: 'forwards' }));
          this._etFadeAnimations = animations;

          await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));

          if (this._etFadeAnimations === animations) {
            this._etFadeAnimations = null;
          }
        }

        // A newer cart change arrived during the fade (its handler cancelled the fade): don't overwrite it.
        if (generation !== this._etRenderGeneration) {
          return;
        }

        this._etReplaceAll(nextDrawer);

        const emptyState = this.querySelector('.et-cart-drawer__empty');

        if (emptyState && !reduceMotion) {
          emptyState.animate({ opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0)'] }, { duration: 150 });
        }
      }

      _etSwapRegion(selector, nextDrawer) {
        const current = this.querySelector(`:scope > ${selector}`);
        const next = nextDrawer.querySelector(`:scope > ${selector}`);

        if (!next) {
          current?.remove();
          return;
        }

        const fragment = toFragment(next.outerHTML);

        if (current) {
          current.replaceWith(fragment);
        } else {
          this.appendChild(fragment);
        }
      }

      _etPatchContent(nextDrawer) {
        const focus = this._etCaptureFocus([
          this.querySelector(':scope > .et-cart-drawer__aside'),
          this.querySelector(':scope > .et-cart-drawer__body')
        ]);

        this._etDestroySwipers(['upsell-aside', 'upsell-inline']);
        this._etSyncHostState(nextDrawer);
        this._etSwapRegion('.et-cart-drawer__aside', nextDrawer);
        this._etSwapRegion('.et-cart-drawer__body', nextDrawer);
        this._etInitSwipers();
        this._etRestoreFocus(focus);
      }

      _etPatchFooter(nextDrawer) {
        const current = this.querySelector(':scope > .et-cart-drawer__footer');
        const next = nextDrawer.querySelector(':scope > .et-cart-drawer__footer');

        if (!next) {
          const focus = this._etCaptureFocus([current]);
          this._etDestroySwipers(['usp']);
          current?.remove();
          this._etRestoreFocus(focus);
          return;
        }

        if (!current) {
          this.appendChild(toFragment(next.outerHTML));
          this._etInitSwipers();
          return;
        }

        const currentParts = Array.from(current.children);
        const nextParts = Array.from(next.children);
        const sameStructure =
          currentParts.length === nextParts.length &&
          currentParts.every((part, index) => part.dataset.etPart === nextParts[index].dataset.etPart);

        if (!sameStructure) {
          const focus = this._etCaptureFocus([current]);
          this._etDestroySwipers(['usp']);
          current.replaceWith(toFragment(next.outerHTML));
          this._etInitSwipers();
          this._etRestoreFocus(focus);
          return;
        }

        const replacements = currentParts
          .map((part, index) => ({ part, nextPart: nextParts[index] }))
          .filter(({ part, nextPart }) => {
            if (part.dataset.etPart === 'note') {
              return false;
            }

            return !(part.dataset.etPart === 'usp' && part._etSource === nextPart.outerHTML);
          });

        const focus = this._etCaptureFocus(replacements.map(({ part }) => part));

        replacements.forEach(({ part, nextPart }) => {
          if (part.dataset.etPart === 'usp') {
            this._etDestroySwipers(['usp']);
          }

          part.replaceWith(toFragment(nextPart.outerHTML));
        });

        this._etInitSwipers();
        this._etRestoreFocus(focus);
      }

      /* Focus: keep keyboard users on the control they used when its region is re-rendered ----------------- */

      _etDescribeFocus(element) {
        const lineItem = element.closest('[data-et-line-key]');
        const card = element.closest('[data-et-product-id]');
        const part = element.closest('[data-et-part]');

        return {
          element,
          control: FOCUS_CONTROLS.find((selector) => element.matches(selector)) || null,
          lineKey: lineItem ? lineItem.getAttribute('data-et-line-key') : null,
          lineItem,
          lineVariantId: lineItem ? lineItem.getAttribute('data-et-variant-id') : null,
          lineNeighbours: lineItem ? neighbourKeys(lineItem, 'data-et-line-key') : [],
          productId: card ? card.getAttribute('data-et-product-id') : null,
          cardNeighbours: card ? neighbourKeys(card, 'data-et-product-id') : [],
          upsellLayout: card?.closest('.et-cart-drawer__upsell--inline') ? 'inline' : 'aside',
          part: part ? part.dataset.etPart : null
        };
      }

      _etOnFocusIn(event) {
        const target = event.target;

        if (target === this || !(target instanceof HTMLElement) || !this.contains(target)) {
          return;
        }

        this._etFocusRecord = this._etDescribeFocus(target);
      }

      /* A control the user deliberately left (focus moved to nothing, e.g. the iOS keyboard's Done) must not be refocused
         by a later re-render. Checked after the event: a control removed while focused, a window switch (focus stays on
         the control) and the Safari click path (pointerdown already recorded the new button) all keep their record. */
      _etOnFocusOut(event) {
        const record = this._etFocusRecord;

        if (event.relatedTarget || !record || record.element !== event.target) {
          return;
        }

        setTimeout(() => {
          const active = document.activeElement;
          const focusIsNowhere = !active || active === document.body || active === document.documentElement;

          if (this._etFocusRecord === record && record.element.isConnected && focusIsNowhere) {
            this._etFocusRecord = null;
          }
        });
      }

      /* Browsers that don't focus buttons on click (Safari) still get a record of the control that was used. */
      _etOnPointerDown(event) {
        const control = event.target.closest?.(FOCUSABLE_SELECTOR);

        if (control && control !== this && this.contains(control)) {
          this._etFocusRecord = this._etDescribeFocus(control);
        } else {
          this._etFocusRecord = null;
        }
      }

      _etOnToggle() {
        if (!this.open) {
          this._etFocusRecord = null;
        }

        this._etSyncAutoplay();
      }

      /* Returns what to restore when focus sits in a region about to be replaced, or was lost when its element
         disappeared (e.g. a removed line item). Focus that lives anywhere else is left alone. */
      _etCaptureFocus(regions) {
        if (!this.open) {
          return null;
        }

        const active = document.activeElement;

        if (active && active !== document.body && active !== document.documentElement) {
          if (active === this) {
            return null;
          }

          return regions.some((region) => region && region.contains(active)) ? this._etDescribeFocus(active) : null;
        }

        const record = this._etFocusRecord;

        if (!record) {
          return null;
        }

        // Focus is on the body: the control that was used either disappeared, or sits in a region about to be replaced
        // but never received focus (Safari doesn't focus buttons on click).
        return !record.element.isConnected || regions.some((region) => region && region.contains(record.element)) ? record : null;
      }

      _etRestoreFocus(record) {
        if (!record || !this.open) {
          return;
        }

        const active = document.activeElement;

        // Something else took focus in the meantime (not the body): respect it.
        if (active && active !== document.body && active !== document.documentElement && active.isConnected) {
          return;
        }

        const target = this._etFindFocusTarget(record);

        if (!target) {
          return null;
        }

        // Native focus scrolling would also scroll a Swiper's overflow-hidden container sideways: reveal it manually.
        target.focus({ preventScroll: true });
        this._etRevealFocus(target);
        return target;
      }

      /* Bring the focused control fully into view with its focus ring: move its slider to the slide (once the Swiper
         exists, see _etInitSwipers), then scroll the drawer part it is slotted into (aside or body) by the overflow. */
      _etRevealFocus(target) {
        if (!target || !this.open || document.activeElement !== target) {
          return;
        }

        const container = target.closest('[data-et-swiper]');
        const swiper = container ? this._etSwipers.get(container) : null;
        const slide = swiper ? target.closest('.swiper-slide') : null;

        if (slide) {
          const index = Array.from(swiper.slides).indexOf(slide);
          const viewport = container.getBoundingClientRect();
          const box = target.getBoundingClientRect();

          if (index > -1 && (box.left < viewport.left - 0.5 || box.right > viewport.right + 0.5)) {
            swiper.slideTo(index, 0);
          }
        }

        let region = target;

        while (region.parentElement && region.parentElement !== this) {
          region = region.parentElement;
        }

        const part = region.parentElement === this ? region.assignedSlot?.parentElement : null;

        if (!part || part.scrollHeight <= part.clientHeight) {
          return;
        }

        const bounds = part.getBoundingClientRect();
        const box = target.getBoundingClientRect();
        const overflowTop = bounds.top + FOCUS_REVEAL_MARGIN - box.top;
        const overflowBottom = box.bottom - (bounds.bottom - FOCUS_REVEAL_MARGIN);

        if (overflowTop > 0) {
          part.scrollTop -= overflowTop;
        } else if (overflowBottom > 0) {
          // A control taller than the part keeps its top edge in view.
          part.scrollTop += Math.min(overflowBottom, -overflowTop);
        }
      }

      _etControlIn(container, control) {
        if (!container) {
          return null;
        }

        const element = control ? (container.matches(control) ? container : container.querySelector(control)) : null;

        return isFocusable(element) ? element : null;
      }

      _etLineTitle(lineItem) {
        const candidates = lineItem ? [lineItem.querySelector('a.et-cart-drawer__line-item-title'), lineItem.querySelector('input[data-line-key]')] : [];

        return candidates.find(isFocusable) || null;
      }

      _etFindFocusTarget(record) {
        const lineByKey = (key) => this.querySelector(`[data-et-line-key="${CSS.escape(key)}"]`);

        if (record.lineKey) {
          const line = lineByKey(record.lineKey);
          const same = this._etControlIn(line, record.control) || (line ? this._etLineTitle(line) : null);

          if (same) {
            return same;
          }

          // The key is gone but the line was not removed: the cart re-keyed it (an automatic discount splitting or merging
          // it, a selling plan or property change). Stay on that variant, trying lines whose key is new since the change
          // before same-variant lines the user did not touch, then cart order.
          if (!line && record.lineVariantId && record.lineItem.dataset.etRemoving !== 'true') {
            const variantLines = Array.from(this.querySelectorAll(`[data-et-variant-id="${CSS.escape(record.lineVariantId)}"]`));
            const isNew = (node) => !record.lineNeighbours.includes(node.getAttribute('data-et-line-key'));
            const ordered = [...variantLines.filter(isNew), ...variantLines.filter((node) => !isNew(node))];
            const variantTarget =
              ordered.map((node) => this._etControlIn(node, record.control)).find(Boolean) ||
              ordered.map((node) => this._etLineTitle(node)).find(Boolean);

            if (variantTarget) {
              return variantTarget;
            }
          }

          for (const key of record.lineNeighbours) {
            const neighbour = this._etLineTitle(lineByKey(key));

            if (neighbour) {
              return neighbour;
            }
          }
        } else if (record.productId) {
          const scope = this.querySelector(`.et-cart-drawer__upsell--${record.upsellLayout}`);
          const cardById = (id) => scope?.querySelector(`[data-et-product-id="${CSS.escape(id)}"]`);

          for (const id of [record.productId, ...record.cardNeighbours]) {
            const card = cardById(id);
            const target = this._etControlIn(card, record.control) || this._etControlIn(card, '.et-cart-drawer__upsell-button');

            if (target) {
              return target;
            }
          }
        } else if (record.part) {
          const part = this.querySelector(`[data-et-part="${CSS.escape(record.part)}"]`);
          const target = this._etControlIn(part, record.control) || Array.from(part?.querySelectorAll(FOCUSABLE_SELECTOR) || []).find(isFocusable);

          if (target) {
            return target;
          }
        } else if (record.control) {
          const target = this._etControlIn(this, record.control);

          if (target) {
            return target;
          }
        }

        const fallbacks = [
          this._etLineTitle(this.querySelector('[data-et-line-key]')),
          this._etControlIn(this, '.et-cart-drawer__empty-button'),
          this._etControlIn(this, '.et-cart-drawer__close')
        ];

        return fallbacks.find(Boolean) || null;
      }

      /* Every quantity=0 request (minus at the minimum, Remove link) marks its line, so a line whose key disappears after
         any other request is known to be re-keyed by the cart rather than removed. Any other request clears the mark: a
         removal that failed before the theme could report an error (network failure, non-JSON error page) leaves it set. */
      _etOnLineItemWillChange(event) {
        const lineItem = event.target.closest?.('line-item');

        if (!lineItem) {
          return;
        }

        if (event.detail?.targetQuantity === 0) {
          lineItem.dataset.etRemoving = 'true';
        } else {
          delete lineItem.dataset.etRemoving;
        }
      }

      /* A removed line fades out and detaches itself before the body re-renders: move focus to a neighbour now. */
      _etOnLineItemChange(event) {
        const lineItem = event.target.closest?.('line-item');
        const active = document.activeElement;
        const cart = event.detail?.cart;

        if (!lineItem || event.detail?.quantity !== 0 || !(cart?.item_count > 0) || !lineItem.contains(active)) {
          return;
        }

        // The theme reports quantity 0 whenever the old key is gone. A re-keyed line whose variant is still in the cart
        // keeps its focus record instead, restored to that variant's new line when the body re-renders.
        const variantId = lineItem.dataset.etVariantId;

        if (lineItem.dataset.etRemoving !== 'true' && variantId && cart.items?.some((item) => String(item.variant_id) === variantId)) {
          this._etRekeyPending = true;
          return;
        }

        const siblings = [];

        for (let node = lineItem.nextElementSibling; node; node = node.nextElementSibling) siblings.push(node);
        for (let node = lineItem.previousElementSibling; node; node = node.previousElementSibling) siblings.push(node);

        const target =
          siblings
            .filter((node) => node.matches('line-item') && node.dataset.etRemoving !== 'true')
            .map((node) => this._etLineTitle(node))
            .find(Boolean) || this._etControlIn(this, '.et-cart-drawer__close');

        target?.focus();
      }

      /* Upsells added from inside the open drawer must not open the theme cart notification / drawer on top of it. */
      _etOnVariantAdd(event) {
        if (event.detail && event.target !== this && this.contains(event.target)) {
          event.detail.blockCartDrawerOpening = true;
        }
      }

      /* Order note: the theme cart-note saves on change without coordinating with line changes, so a cart/change.js
         that runs at the same time can keep the old note. Every cart response is checked and a lost note re-saved. */
      _etReconcileNote(cart) {
        const input = this.querySelector('[data-et-part="note"] textarea[name="note"]');

        if (!input || !cart || !Object.prototype.hasOwnProperty.call(cart, 'note')) {
          return null;
        }

        const note = input.value;

        if (note === (cart.note || '')) {
          return null;
        }

        this._etSaveNote(note);
        return note;
      }

      _etSaveNote(note) {
        const save = () =>
          fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note }),
            keepalive: true
          }).catch(() => {});

        this._etNoteQueue = (this._etNoteQueue || Promise.resolve()).then(save);
        return this._etNoteQueue;
      }

      /* A re-rendered note field shows the note being saved, not the stale server value. */
      _etApplyNote(note) {
        const input = this.querySelector('[data-et-part="note"] textarea[name="note"]');

        if (note === null || !input || input.value === note) {
          return;
        }

        input.value = note;
      }

      /* Free shipping bar ------------------------------------------------- */

      _etShippingState(bar) {
        const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
        const threshold = Math.round((parseFloat(bar.dataset.threshold) || 0) * rate);
        const total = parseFloat(bar.dataset.total) || 0;

        return {
          rate,
          threshold,
          total,
          unlocked: total >= threshold,
          progress: threshold > 0 ? Math.min(total / threshold, 1) : 1
        };
      }

      _etPatchShipping(nextDrawer) {
        const header = this.querySelector(':scope > .et-cart-drawer__header');
        const current = header?.querySelector('[data-et-shipping]');
        const next = nextDrawer.querySelector('[data-et-shipping]');

        if (!header) {
          return;
        }

        if (!next) {
          current?.remove();
          return;
        }

        if (!current) {
          header.appendChild(toFragment(next.outerHTML));
          this._etApplyShippingState(header.querySelector('[data-et-shipping]'));
          return;
        }

        current.dataset.threshold = next.dataset.threshold;
        current.dataset.total = next.dataset.total;
        current.dataset.lockedTemplate = next.dataset.lockedTemplate || '';

        const currentLocked = current.querySelector('[data-et-shipping-locked]');
        const nextLocked = next.querySelector('[data-et-shipping-locked]');

        // When the cart unlocks, keep the last real amount in the locked message while it fades out
        // (the new render would read "Spend 0,00 more").
        if (currentLocked && nextLocked && !this._etShippingState(current).unlocked) {
          currentLocked.innerHTML = nextLocked.innerHTML;
        }

        this._etApplyShippingState(current);
      }

      _etApplyShippingState(bar) {
        if (!bar) {
          return;
        }

        const { rate, threshold, total, unlocked, progress } = this._etShippingState(bar);

        bar.classList.toggle('et-cart-drawer__shipping--unlocked', unlocked);
        bar.style.setProperty('--et-shipping-progress', progress.toString());

        const progressBar = bar.querySelector('[role="progressbar"]');
        progressBar?.setAttribute('aria-valuemax', threshold.toString());
        progressBar?.setAttribute('aria-valuenow', Math.min(total, threshold).toString());

        const lockedMessage = bar.querySelector('[data-et-shipping-locked]');
        const unlockedMessage = bar.querySelector('[data-et-shipping-unlocked]');

        lockedMessage?.setAttribute('aria-hidden', unlocked ? 'true' : 'false');
        unlockedMessage?.setAttribute('aria-hidden', unlocked ? 'false' : 'true');

        if (rate !== 1 && lockedMessage && !unlocked && bar.dataset.lockedTemplate) {
          const amount = document.createElement('strong');
          amount.textContent = new Intl.NumberFormat(window.Shopify.locale, {
            style: 'currency',
            currency: window.Shopify.currency.active
          }).format((threshold - total) / 100);

          lockedMessage.innerHTML = bar.dataset.lockedTemplate.split('[amount]').join(amount.outerHTML);
        }
      }

      /* Quantity stepper: minus at the minimum quantity removes the line --- */

      _etOnStepperClick(event) {
        const button = event.target.closest?.('[data-et-stepper="minus"]');

        if (!button || !this.contains(button)) {
          return;
        }

        const input = button.closest('quantity-selector')?.querySelector('input[data-line-key]');
        const lineItemQuantity = button.closest('line-item-quantity');

        if (!input || typeof lineItemQuantity?._changeLineItemQuantity !== 'function') {
          return;
        }

        const minimum = parseInt(input.min, 10) || 1;

        if (parseInt(input.value, 10) > minimum) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const lineItem = button.closest('line-item');
        const isBusy = lineItem?.querySelector('pill-loader')?.getAttribute('aria-busy') === 'true';

        // A removal is already in flight (or animating out): don't send a duplicate quantity=0 request.
        if (isBusy || lineItem?.dataset.etRemoving === 'true') {
          return;
        }

        if (lineItem) {
          lineItem.dataset.etRemoving = 'true';
        }

        lineItemQuantity._changeLineItemQuantity(input.dataset.lineKey, 0);
      }

      _etOnLineItemError(event) {
        const lineItem = event.target.closest?.('line-item');

        if (lineItem) {
          delete lineItem.dataset.etRemoving;
        }
      }

      /* Swiper -------------------------------------------------------------- */

      _etInitSwipers() {
        const containers = Array.from(this.querySelectorAll('[data-et-swiper]'));

        if (containers.length === 0) {
          return;
        }

        containers.forEach((container) => {
          const uspPart = container.closest('[data-et-part="usp"]');

          if (uspPart && !this._etSwipers.has(container)) {
            uspPart._etSource = uspPart.outerHTML;
          }
        });

        whenSwiperReady().then((Swiper) => {
          if (!Swiper || !this.isConnected) {
            return;
          }

          const created = [];

          this.querySelectorAll('[data-et-swiper]').forEach((container) => {
            const type = container.dataset.etSwiper;

            if (this._etSwipers.has(container)) {
              return;
            }

            if ((type === 'upsell-aside' && this._etMediaQuery.matches) || (type === 'upsell-inline' && !this._etMediaQuery.matches)) {
              return;
            }

            this._etSwipers.set(container, new Swiper(container, this._etSwiperOptions(type, container)));
            created.push(container);
          });

          // Swiper only scrolls a slide into view on focus events it sees, and focus restored after a re-render lands
          // before the new instance exists: show that slide now. Existing sliders the user swiped are left alone.
          const active = document.activeElement;

          if (created.some((container) => container.contains(active))) {
            this._etRevealFocus(active);
          }

          this._etApplyPendingSlide();
          this._etSyncAutoplay();
        });
      }

      _etSwiperOptions(type, container) {
        const reduceMotion = prefersReducedMotion();
        const slideCount = container.querySelectorAll('.swiper-slide').length;
        const paginationElement = container.parentElement.querySelector(':scope > [data-et-swiper-pagination]');
        const options = {
          speed: reduceMotion ? 0 : 400,
          watchOverflow: true,
          // Slides keep their natural height, so a swipe can start in the empty space under a shorter card.
          touchEventsTarget: 'container',
          // Swiper only matches the pressed element itself: without 'button *' a press on a button's label or icon is
          // default-prevented, so the button never takes focus and the previously focused control is blurred.
          focusableElements: 'input, select, option, textarea, button, video, label, button *',
          a11y: container.dataset.etBulletLabel ? { paginationBulletMessage: container.dataset.etBulletLabel } : {}
        };

        if (paginationElement) {
          options.pagination = {
            el: paginationElement,
            clickable: true,
            bulletElement: 'button',
            bulletClass: 'et-cart-drawer__dot',
            bulletActiveClass: 'et-cart-drawer__dot--active',
            modifierClass: 'et-cart-drawer__pagination--',
            clickableClass: 'et-cart-drawer__pagination--clickable',
            lockClass: 'et-cart-drawer__pagination--lock'
          };
        }

        switch (type) {
          case 'usp': {
            // usp_rotation_speed, in seconds. A missing or unparseable value keeps the original 4s.
            const uspDelay = Number(container.dataset.etUspDelay) * 1000 || USP_AUTOPLAY_DELAY;

            return {
              ...options,
              effect: 'fade',
              fadeEffect: { crossFade: true },
              slidesPerView: 1,
              rewind: true,
              // Any swipe or dot click stops the rotation; focus and pointer interaction stop it too (_etOnUspInteraction).
              autoplay: reduceMotion ? false : { delay: uspDelay, pauseOnMouseEnter: true, disableOnInteraction: true }
            };
          }

          case 'upsell-aside':
            // A single card fills the column instead of peeking at a slide that doesn't exist.
            return slideCount > 1 ? { ...options, slidesPerView: 'auto', spaceBetween: 12 } : { ...options, slidesPerView: 1 };

          default:
            return { ...options, slidesPerView: slideCount > 1 ? 1.12 : 1, spaceBetween: 8 };
        }
      }

      _etSyncAutoplay() {
        if (!this.open) {
          this._etUspUserPaused = false;
        }

        const shouldRun = this.open && !this._etBlockSelected && !this._etUspUserPaused;

        this._etSwipers.forEach((swiper) => {
          if (!swiper.autoplay || !swiper.params.autoplay || !swiper.params.autoplay.enabled) {
            return;
          }

          if (shouldRun && !swiper.autoplay.running) {
            swiper.autoplay.start();
          } else if (!shouldRun && swiper.autoplay.running) {
            swiper.autoplay.stop();
          }
        });
      }

      /* WCAG 2.2.2: keyboard focus or a tap/click inside the USP bar stops the rotation until the drawer closes. */
      _etOnUspInteraction(event) {
        const usp = event.target.closest?.('[data-et-part="usp"]');

        if (!usp || !this.contains(usp)) {
          return;
        }

        this._etUspUserPaused = true;
        this._etSwipers.get(usp.querySelector('[data-et-swiper="usp"]'))?.autoplay?.stop();
      }

      _etDestroySwipers(types) {
        this._etSwipers.forEach((swiper, container) => {
          if (types && !types.includes(container.dataset.etSwiper)) {
            return;
          }

          swiper.destroy(true, true);
          this._etSwipers.delete(container);
        });
      }

      /* Theme editor ------------------------------------------------------ */

      _etShowSlide(swiper, slide) {
        swiper.autoplay?.stop();

        const index = Array.from(swiper.slides).indexOf(slide);

        if (index > -1) {
          swiper.slideTo(index, 0);
        }
      }

      _etApplyPendingSlide() {
        const slide = this._etPendingSlide;

        if (!slide) {
          return;
        }

        if (!slide.isConnected) {
          this._etPendingSlide = null;
          return;
        }

        const swiper = this._etSwipers.get(slide.closest('[data-et-swiper="usp"]'));

        if (swiper) {
          this._etPendingSlide = null;
          this._etShowSlide(swiper, slide);
        }
      }

      _etOnBlockSelect(event) {
        const slide = event.target.closest?.('.et-cart-drawer__usp-item');
        const container = slide?.closest('[data-et-swiper="usp"]');

        if (!container) {
          return;
        }

        this._etBlockSelected = true;

        const swiper = this._etSwipers.get(container);

        if (swiper) {
          this._etPendingSlide = null;
          this._etShowSlide(swiper, slide);
        } else {
          // Swiper is still loading (e.g. the second USP block was just added): apply once it is created.
          this._etPendingSlide = slide;
        }
      }

      _etOnBlockDeselect() {
        this._etBlockSelected = false;
        this._etPendingSlide = null;
        this._etSyncAutoplay();
      }
    }

    window.customElements.define('et-cart-drawer', EtCartDrawer);
  };

  window.customElements.whenDefined('cart-drawer').then(defineEtCartDrawer);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('et-cart-drawer').forEach((drawer) => {
      if (typeof drawer._etAfterRender === 'function') {
        drawer._etAfterRender();
      }
    });
  });
})();
